import { useTranslation } from '@/hooks/useTranslation';
import { Modal } from './Modal';
import { useState } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { useModalContext } from '@/hooks/useModalContext';
import { InputPasteButton } from '../InputPasteButton';
import { useCreateMWList } from '@/hooks/message-whale/lists/useCreateMWList';
import { toast } from 'sonner';
import { LoadingIndicator } from '../common/LoadingIndicator';
import { useCommonStore } from '@/stores/common';
import { useOrganizationStore } from '@/stores/organization';
import { Switch } from '../ui/switch';
import { MWListForCreate } from '@/types/message-whale/mwList';
import { ErrorBag } from '@/types/error';
import { parseAxiosError } from '@/utils/error';
import { FormErrorDisplay } from '../common/FormErrorDisplay';
import { integrations } from '@/integrations';

export const CreateMWListModal = () => {
  const { t } = useTranslation('common');
  const { closeModal } = useModalContext();
  const { organization } = useOrganizationStore();
  const [errors, setErrors] = useState<ErrorBag>({});

  const isOnboarding = useCommonStore(state => state.isOnboarding);

  const handleClose = (isSuccess?: boolean) => {
    closeModal('createNewMWList', isSuccess ?? false);
  };

  const [name, setName] = useState('');
  const [syncIntegrationSubscribers, setSyncIntegrationSubscribers] = useState(false);
  const [syncExistingSubscribers, setSyncExistingSubscribers] = useState(false);

  const { mutate: createList, isPending: isCreatingList } = useCreateMWList({
    onSuccess: () => {
      toast.success(t('listCreatedSuccessfully'));
      handleClose(true);
      setName('');
    },
    onError: (error) => {
      const parsedErrors = parseAxiosError(error);
      if (parsedErrors) {
        setErrors(parsedErrors.errors);
      }
      toast.error(t('failedToCreateList'));
    },
  });

  const handleSave = () => {
    const data: Partial<MWListForCreate> = {
      name,
    };

    if (organization?.integration !== '') {
      data.syncIntegrationSubscribers = syncIntegrationSubscribers;
    }

    createList(data);
  };

  const handlePaste = async (
    setValue: React.Dispatch<React.SetStateAction<string>>
  ) => {
    try {
      const text = await navigator.clipboard.readText();
      setValue(text);
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
    }
  };

  return (
    <Modal modalType="createNewMWList" title={t('createNewList')}
      description={isOnboarding ? t('createNewListDescription') : undefined}
    >
      <div className="space-y-4">
        <div className="relative">
          <Label htmlFor="list-name">{t('name')}</Label>
          <Input
            id="list-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <InputPasteButton onClick={() => handlePaste(setName)} />
          <FormErrorDisplay field="name" error={errors?.name} />
        </div>

        {
          isOnboarding && integrations.includes(organization?.integration ?? '') && (
            <>
              <div className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label htmlFor="sync-subscribers">
                    <span>{t('syncShopSubscribers')}</span>

                    <div className="text-sm text-muted-foreground">
                      {t('syncShopSubscribersDescription')}
                    </div>
                  </Label>
                </div>
                <Switch id="sync-subscribers" checked={syncIntegrationSubscribers} onCheckedChange={setSyncIntegrationSubscribers} />
              </div>

              <div className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label htmlFor="sync-subscribers">
                    <span>{t('syncExistingSubscribers')}</span>

                    <div className="text-sm text-muted-foreground">
                      {t('syncExistingSubscribersDescription')}
                    </div>
                  </Label>
                </div>
                <Switch id="sync-subscribers" checked={syncExistingSubscribers} onCheckedChange={setSyncExistingSubscribers}
                  disabled={!syncIntegrationSubscribers}
                />
              </div>
            </>
          )
        }

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={() => handleClose()}>
            {t('close')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isCreatingList}
            className="flex gap-1"
            variant="conversionCraft"
          >
            {isCreatingList && <LoadingIndicator size="small" />}
            {t('save')}
          </Button>
        </DialogFooter>
      </div>
    </Modal>
  );
};
